import React from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/v2/components/ui/avatar";
import { Skeleton } from "@/v2/components/ui/skeleton";
import PageRoutes from "@/utils/constants";
import type { ScheduleGame } from "./api";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const userTimeZone = dayjs.tz.guess();

type ScheduleTableProps = {
  games: ScheduleGame[];
  isLoading: boolean;
  isError: boolean;
  limit?: number;
};

function toTitleCase(str?: string): string {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatGameDate(dateStr?: string): string {
  if (!dateStr) return "";
  const d = dayjs(dateStr);
  return d.isValid() ? d.format("MMM. DD") : "";
}

function formatGameTime(dateTimeStr?: string): string {
  if (!dateTimeStr) return "";
  try {
    // dayjs's "a" parse token only matches a lowercase am/pm, unlike moment's
    // case-insensitive one, so normalize the meridiem first or an uppercase
    // "PM" silently mis-parses (the hour is read as-is, never bumped by 12).
    const normalized = dateTimeStr.replace(/\b(AM|PM)\b/i, (match) => match.toLowerCase());
    // Parse WITH the embedded offset ("Z" token, matches the v1 moment
    // format "YYYY-MM-DD hh:mm:ss.SSSSSS a Z") so the instant comes from the
    // string's own offset. Passing "America/New_York" as a third dayjs.tz
    // argument here would instead re-interpret the parsed wall-clock values
    // as already being New York local time, double-applying the timezone
    // and shifting every displayed time by several hours.
    const d = dayjs(normalized, "YYYY-MM-DD hh:mm:ss.SSSSSS a Z");
    if (!d.isValid()) return "";
    return d.tz(userTimeZone).format("h:mm A").toUpperCase();
  } catch {
    return "";
  }
}

export function ScheduleTable({ games, isLoading, isError, limit = 5 }: ScheduleTableProps) {
  const navigate = useNavigate();

  const handleNavigate = (gameId: string | number) => {
    navigate(PageRoutes.Watch.replace(":gameId", String(gameId)));
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="mx-auto mb-2 h-8 w-full last:mb-0" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-destructive">Failed to load games.</p>
      </div>
    );
  }

  const visibleGames = games.slice(0, limit);

  return (
    <div className="v2-schedule-table-wrapper" style={{ border: '1px solid var(--v2-border)', borderRadius: '8px', overflowX: 'auto', backgroundColor: 'var(--v2-background)' }}>
      <table style={{ width: '100%', minWidth: '500px', borderCollapse: 'collapse' }}>
        <tbody>
          {visibleGames.length > 0 ? (
            visibleGames.map((row, index) => (
              <tr
                key={row.game_id}
                onClick={() => handleNavigate(row.game_id)}
                style={{
                  cursor: 'pointer',
                  backgroundColor: 'var(--v2-background)',
                  borderBottom: index < visibleGames.length - 1 ? '1px solid var(--v2-border)' : 'none',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--v2-accent)')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--v2-background)')}
              >
                {/* Date + Time */}
                <td style={{ padding: '10px 8px 10px 12px', verticalAlign: 'middle', width: '95px', minWidth: '95px', whiteSpace: 'nowrap' }}>
                  <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                    {formatGameDate(row.game_date)}
                  </p>
                  <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                    {formatGameTime(row.game_date_time)}
                  </p>
                </td>

                {row.card_image_url_wide ? (
                  <td style={{ padding: '10px 8px', verticalAlign: 'middle', width: '160px' }} colSpan={2}>
                    <img
                      src={row.card_image_url_wide}
                      alt={`Game ${row.game_id}`}
                      style={{ height: '46px', width: '100%', borderRadius: '6px', objectFit: 'contain' }}
                    />
                  </td>
                ) : (
                  <>
                    {/* Away Team */}
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', width: '140px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar className="shrink-0" style={{ width: '36px', height: '36px' }}>
                          {row.away_team_logo ? <AvatarImage src={row.away_team_logo} alt={row.away_team ?? ''} /> : null}
                          <AvatarFallback style={{ fontSize: '10px' }}>{row.away_team?.[0] ?? '?'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                            {toTitleCase(row.away_team)}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                            {toTitleCase(row.away_team_mascot)}
                          </p>
                        </div>
                      </div>
                    </td>
                    {/* Home Team */}
                    <td style={{ padding: '10px 8px', verticalAlign: 'middle', width: '140px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Avatar className="shrink-0" style={{ width: '36px', height: '36px' }}>
                          {row.home_team_logo ? <AvatarImage src={row.home_team_logo} alt={row.home_team ?? ''} /> : null}
                          <AvatarFallback style={{ fontSize: '10px' }}>{row.home_team?.[0] ?? '?'}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                            {toTitleCase(row.home_team)}
                          </p>
                          <p style={{ fontSize: '12px', color: 'var(--v2-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0 }}>
                            {toTitleCase(row.home_team_mascot)}
                          </p>
                        </div>
                      </div>
                    </td>
                  </>
                )}

                {/* Venue */}
                <td style={{ padding: '10px 12px 10px 8px', verticalAlign: 'middle' }}>
                  <p style={{ fontSize: '12px', color: 'var(--v2-muted-foreground)', fontWeight: 400, fontStyle: 'normal', textTransform: 'none', lineHeight: '1.4', margin: 0, maxWidth: '120px' }}>
                    {toTitleCase(row.facility_name)}
                  </p>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} style={{ padding: '16px', textAlign: 'center', verticalAlign: 'middle' }}>
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>No games found</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

